import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { Status } from 'components/Status';
import { Label, Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { DisbursementMethod, IPayoutStateByStatus, IPayoutStats, PayoutStatus } from 'types';
import { sumBy } from 'utils/arrays';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';

interface IPayoutStatsProps {
  stats?: IPayoutStats;
  isLoading: boolean;
}

const Layout = styled.section`
  width: 100%;
  margin-bottom: ${toRem(24)};
  padding: 0 0 0 ${toRem(theme.layout.gutter.small)};

  @media (min-width: ${theme.breakpoint.med}px) {
    padding: 0;
  }
`;

const PaymentTotals = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  border-radius: ${toRem(11)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(20)} !important;
`;

const TotalSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;

  label {
    text-transform: uppercase;
    font-size: ${toRem(11)};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    &:first-child {
      border-right: ${toRem(1)} solid ${theme.color.neutral[200]};
    }
  }
`;

const TotalRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  margin-top: ${toRem(16)};
  height: 100%;
  justify-content: space-between;

  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row nowrap;
  }

  label,
  .status-label {
    text-transform: capitalize;
    font-size: ${toRem(11)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};
  }
`;

const TotalValue = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;

  span.value {
    margin-top: ${toRem(4)};
  }

  p {
    font-size: ${toRem(16)};
    margin-top: ${toRem(4)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

const statusCount = (statsByStatus: IPayoutStateByStatus[], status: PayoutStatus) => {
  const byStatus = (statsByStatus || []).filter((s) => s.status === status);
  return sumBy(byStatus, 'count');
};

const netTotalByStatus = (statsByStatus: IPayoutStateByStatus[], status: PayoutStatus) => {
  const byStatus = (statsByStatus || []).filter((s) => s.status === status);
  return sumBy(byStatus, 'net');
};

export const PayoutStats = ({ stats, isLoading }: IPayoutStatsProps) => {
  const statsByDate = stats?.byDate;
  const grossTotal = statsByDate ? sumBy(statsByDate, 'gross') : 0;
  const netTotal = statsByDate ? sumBy(statsByDate, 'net') : 0;
  const holdbackTotal = statsByDate ? sumBy(statsByDate, 'holdbacks') : 0;
  const instantTotal = statsByDate ? sumBy(statsByDate, DisbursementMethod.INSTANT) : 0;
  const payrollTotal = statsByDate ? sumBy(statsByDate, DisbursementMethod.PAYROLL) : 0;

  const statsByStatus = stats?.byStatus;
  const paidCount = statusCount(statsByStatus, PayoutStatus.PAID);
  const pendingCount = statusCount(statsByStatus, PayoutStatus.PENDING);
  const expiredCount = statusCount(statsByStatus, PayoutStatus.EXPIRED);
  const canceledCount = statusCount(statsByStatus, PayoutStatus.CANCELED);

  const paidTotal = netTotalByStatus(statsByStatus, PayoutStatus.PAID);
  const pendingTotal = netTotalByStatus(statsByStatus, PayoutStatus.PENDING);
  const expiredTotal = netTotalByStatus(statsByStatus, PayoutStatus.EXPIRED);
  const canceledTotal = netTotalByStatus(statsByStatus, PayoutStatus.CANCELED);

  return (
    <Layout data-cy="stats-section">
      <PaymentTotals data-cy="payment-totals">
        <TotalSection>
          {isLoading ? <Skeleton width={100} /> : <Label>Payment Overview</Label>}
          <TotalRow>
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="stats-gross">
                <Label>Gross</Label>
                <Text size="small">{centsToDollarsDisplay(grossTotal)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="stats-holdback">
                <Label>Holdback</Label>
                <Text size="small">{centsToNegativeDollarsDisplay(holdbackTotal)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="stats-net">
                <Label>Net</Label>
                <Text size="small">{centsToDollarsDisplay(netTotal)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="stats-payroll">
                <Label>Payroll</Label>
                <Text size="small">{centsToDollarsDisplay(payrollTotal)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="stats-instant">
                <Label>Instant</Label>
                <Text size="small">{centsToDollarsDisplay(instantTotal)}</Text>
              </TotalValue>
            )}
          </TotalRow>
        </TotalSection>
        <TotalSection>
          {isLoading ? <Skeleton width={100} /> : <Label>Payment Status</Label>}
          <TotalRow>
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="status-paid">
                <Status appearance={Appearance.SUCCESS} label={`Paid (${paidCount || 0})`} isLoading={isLoading} className="status-label" />
                <Text size="small">{centsToDollarsDisplay(paidTotal || 0)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="status-pending">
                <Status appearance={Appearance.WARNING} label={`Pending (${pendingCount || 0})`} isLoading={isLoading} className="status-label" />
                <Text size="small">{centsToDollarsDisplay(pendingTotal || 0)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="status-canceled">
                <Status appearance={Appearance.NEUTRAL} label={`Canceled (${canceledCount || 0})`} isLoading={isLoading} className="status-label" />
                <Text size="small">{centsToDollarsDisplay(canceledTotal || 0)}</Text>
              </TotalValue>
            )}
            {isLoading ? (
              <TotalValue>
                <Skeleton width={100} />
                <Skeleton width={100} className="value" />
              </TotalValue>
            ) : (
              <TotalValue data-cy="status-expired">
                <Status appearance={Appearance.DANGER} label={`Expired (${expiredCount || 0})`} isLoading={isLoading} className="status-label" />
                <Text size="small">{centsToDollarsDisplay(expiredTotal || 0)}</Text>
              </TotalValue>
            )}
          </TotalRow>
        </TotalSection>
      </PaymentTotals>
    </Layout>
  );
};
